import {expectEither, HttpClient, useHttpClient} from '@raison/network-facade';
import {Wish} from '@raison/prelude';
import {AccountId, BaseCurrencyCode, CryptoCurrencyCode} from '@raison/types';
import {queryOptions} from '@tanstack/react-query';
import {CryptoCurrencyType} from '~/entities/currencies';
import {MAX_DECIMALS_IN_CRYPTO} from '../../../config';
import {TrTx, TrTxId, TrTxStatusWish} from '../model/TrTx';

type TrtDto = typeof TrtDtoWish.Type;
const TrtDtoWish = Wish.struct({
    id: TrTxId,
    originator_account: Wish.struct({
        id: AccountId,
        blockchain: Wish.struct({
            network_name: Wish.string,
            currency: CryptoCurrencyCode,
        }),
    }),
    status: TrTxStatusWish,
    amount: Wish.BigNumberFromString,
    currency: BaseCurrencyCode,
    beneficiary_wallet: Wish.string,
    comment: Wish.nullOr(Wish.string),
    beneficiary_legal_form: Wish.literal('company', 'individual'),
    details: Wish.struct({
        name: Wish.string,
        value: Wish.nullOr(Wish.string),
    }).pipe(Wish.array),
});

interface ICryptoCurrencyInfo {
    code: CryptoCurrencyCode;
    decimals: number;
    name: string;
    type: CryptoCurrencyType;
}

export function enrichTrtData(trt: TrtDto, currencies: ICryptoCurrencyInfo[]): TrTx {
    const currencyInfo = currencies.find(i => i.code === trt.currency);
    if (!currencyInfo) {
        throw new Error(`Expected to find currency info for ${trt.currency}`);
    }

    return {
        id: trt.id,
        status: trt.status,
        currency: {
            code: trt.currency,
            decimals: Math.min(currencyInfo.decimals, MAX_DECIMALS_IN_CRYPTO),
        },
        fromAccount: {
            ...trt.originator_account,
            blockchain: {
                networkName: trt.originator_account.blockchain.network_name,
                currencyCode: trt.originator_account.blockchain.currency,
                currencyDecimals: Math.min(currencyInfo.decimals, MAX_DECIMALS_IN_CRYPTO),
            },
            name: currencyInfo.name,
            type: currencyInfo.type,
        },

        toAddress: trt.beneficiary_wallet,
        amount: trt.amount,
        comment: trt.comment,
        additionalDetails: trt.details.filter(
            (d): d is typeof d & {value: NonNullable<typeof d.value>} => d.value !== null
        ),
    };
}

export function fetchTrTxDetails(http: HttpClient, trtId: TrTxId) {
    return http(`/v1/travel_rule/transactions/${trtId}`).then(
        ...expectEither({
            200: Wish.toStandardSchemaV1(TrtDtoWish),
            404: null,
        })
    );
}

export function useTrTxDetailsOptions(trtId: TrTxId) {
    const http = useHttpClient();
    const {queryKey, queryFn} = queryOptions({
        queryKey: ['/v1/travel_rule/transactions/', trtId] as const,
        queryFn: async ({client, queryKey}) => {
            const response = await fetchTrTxDetails(http, trtId);
            switch (response.status) {
                case 200:
                    return response.json;
                case 404:
                    return null;
                case 'Abort':
                    await client.cancelQueries({queryKey});
                    throw response.reason;
            }
        },
    });

    return {queryKey, queryFn};
}
